import { useState, useEffect, useCallback, useRef } from 'react';
import { pb, getRestaurantId } from '../config/pocketbase';

/**
 * Hook Realtime PocketBase per Tap2Order
 * Sottoscrizione SSE a latenza zero (<50ms) per Menu e Comande.
 * 
 * @param {string} table - 'menu' o 'comande'
 * @param {object} filter - Filtri opzionali (es. { status: 'attiva' })
 * @returns {object} { data, loading, error, refresh }
 */
export const usePocketBaseRealtime = (table, filter = null) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const restaurantId = getRestaurantId();
  
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const collectionMap = {
    'menu': 'menu_tap2order',
    'comande': 'comande_tap2order'
  };
  const actualCollection = collectionMap[table] || table;

  // Costruisce la query di filtro PocketBase
  const buildFilterString = useCallback(() => {
    const parts = [];
    
    // Filtro per multi-tenancy:
    // Se siamo su ristorante-2, filtriamo per ristorante-2
    // Se siamo su ristorante-1 o default o non specificato, accettiamo tutte le comande del locale principale
    if (restaurantId && restaurantId === 'ristorante-2') {
      parts.push(`ristorante_id = "ristorante-2"`);
    }

    if (filterRef.current) {
      Object.keys(filterRef.current).forEach(key => {
        const val = filterRef.current[key];
        if (typeof val === 'boolean') {
          parts.push(`${key} = ${val}`);
        } else if (typeof val === 'number') {
          parts.push(`${key} = ${val}`);
        } else if (typeof val === 'string') {
          parts.push(`${key} = "${val}"`);
        }
      });
    }

    return parts.length > 0 ? parts.join(' && ') : '';
  }, [restaurantId]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filterStr = buildFilterString();
      const options = {
        filter: filterStr,
        requestKey: null
      };

      if (actualCollection === 'menu_tap2order') {
        options.sort = 'categoria_ordine,nome';
      } else if (actualCollection === 'comande_tap2order') {
        // Usa -timestamp (in PocketBase comande_tap2order usa il campo 'timestamp', non 'created')
        options.sort = '-timestamp';
      }

      let records;
      try {
        records = await pb.collection(actualCollection).getFullList(options);
      } catch (sortErr) {
        // Fallback di sicurezza: se il sort dovesse fallire, riprova senza sort per garantire la lettura dei dati
        console.warn(`[PocketBase] Sort fallito, fallback senza sort:`, sortErr?.message);
        records = await pb.collection(actualCollection).getFullList({
          filter: filterStr,
          requestKey: null
        });
      }
      
      // Normalizzazione campi timestamp per retrocompatibilità
      const normalized = records.map(r => ({
        ...r,
        timestamp: r.timestamp || new Date().toISOString()
      }));

      setData(normalized);
      setLoading(false);
    } catch (err) {
      console.warn(`[PocketBase] Fetch fallito per ${actualCollection}:`, err?.message);
      setError(err?.message || 'Errore di connessione a PocketBase');
      setLoading(false);
    }
  }, [actualCollection, buildFilterString]);

  useEffect(() => {
    let isSubscribed = true;
    let unsubscribeFn = null;

    fetchData();

    // Listener SSE Realtime
    const handleRealtimeEvent = (e) => {
      if (!isSubscribed) return;

      const record = {
        ...e.record,
        timestamp: e.record.timestamp || new Date().toISOString()
      };

      // Controllo multi-tenancy ristorante
      if (restaurantId === 'ristorante-2') {
        if (record.ristorante_id !== 'ristorante-2') return;
      }

      // Verifica filtri addizionali
      const currentFilter = filterRef.current;
      const matchesFilter = !currentFilter || Object.keys(currentFilter).every(k => record[k] === currentFilter[k]);

      setData(current => {
        if (e.action === 'create') {
          if (matchesFilter) {
            if (actualCollection === 'menu_tap2order') {
              const updated = [...current.filter(item => item.id !== record.id), record];
              return updated.sort((a, b) => {
                if (a.categoria_ordine !== b.categoria_ordine) return (a.categoria_ordine || 0) - (b.categoria_ordine || 0);
                return (a.nome || '').localeCompare(b.nome || '');
              });
            }
            return [record, ...current.filter(item => item.id !== record.id)];
          }
          return current;
        } else if (e.action === 'update') {
          if (matchesFilter) {
            const exists = current.some(item => item.id === record.id);
            if (exists) {
              return current.map(item => item.id === record.id ? record : item);
            }
            return [record, ...current];
          } else {
            return current.filter(item => item.id !== record.id);
          }
        } else if (e.action === 'delete') {
          return current.filter(item => item.id !== record.id);
        }
        return current;
      });
    };

    // Sottoscrizione SSE Realtime a latenza zero
    const setupSubscription = async () => {
      try {
        const unsub = await pb.collection(actualCollection).subscribe('*', handleRealtimeEvent);
        if (isSubscribed) {
          unsubscribeFn = unsub;
        } else if (typeof unsub === 'function') {
          unsub();
        }
      } catch (subErr) {
        console.warn(`[PocketBase Realtime] Sottoscrizione SSE non riuscita per ${actualCollection}:`, subErr?.message);
      }
    };

    setupSubscription();

    return () => {
      isSubscribed = false;
      if (typeof unsubscribeFn === 'function') {
        try {
          unsubscribeFn();
        } catch (e) {
          // ignore
        }
      }
    };
  }, [actualCollection, fetchData, restaurantId]);

  return {
    data,
    loading,
    error,
    refresh: fetchData
  };
};

export default usePocketBaseRealtime;
